import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form } from 'react-bootstrap';
import { 
  BarChart3, 
  DollarSign, 
  FileText, 
  Calendar,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  PlusCircle,
  Edit2
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import './Invoice.css';

function InvoiceDashboard() {
  const stats = [
    { title: "Total Facturé", value: "5,500 €", change: "+15%", trend: "up", icon: DollarSign },
    { title: "Nbre Factures", value: "45", change: "+8%", trend: "up", icon: FileText },
    { title: "Moy. Mensuelle", value: "1,375 €", change: "+12%", trend: "up", icon: Calendar },
    { title: "Croissance", value: "+67%", change: "+5%", trend: "up", icon: TrendingUp },
  ];

  const monthlyInvoices = [
    { month: "Janvier", total: 1200 },
    { month: "Février", total: 800 },
    { month: "Mars", total: 1500 },
    { month: "Avril", total: 2000 },
  ];

  const invoiceDistribution = [
    { range: "0-500 €", value: 40 },
    { range: "500-1000 €", value: 25 },
    { range: "1000-2000 €", value: 20 },
    { range: ">2000 €", value: 15 },
  ];

  const [invoices, setInvoices] = useState([
    { id: 1, date: '2025-01-15', amount: '500 €', status: 'Payée' },
    { id: 2, date: '2025-01-20', amount: '1200 €', status: 'En attente' },
    { id: 3, date: '2025-02-01', amount: '700 €', status: 'Payée' },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const handleOpenModal = (invoice) => {
    setCurrentInvoice(invoice);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentInvoice(null);
    setShowModal(false);
  };

  const handleSaveInvoice = (event) => {
    event.preventDefault();
    // Ajoutez ici la logique pour sauvegarder la facture (création ou modification)
    handleCloseModal();
  };

  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3 mb-0">Statistiques des Factures</h1>
          <small className="text-muted">Dernière mise à jour: {new Date().toLocaleDateString()}</small>
        </div>

        {/* Stats Grid */}
        <Row className="g-4 mb-4">
          {stats.map((stat, index) => (
            <Col key={index} xs={12} md={6} lg={3}>
              <Card className="stat-card h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="icon-container">
                      <stat.icon size={24} />
                    </div>
                    <span className={`d-flex align-items-center ${
                      stat.trend === 'up' ? 'text-success' : 'text-danger'
                    }`}>
                      {stat.change}
                      {stat.trend === 'up' ? (
                        <ArrowUpRight size={16} className="ms-1" />
                      ) : (
                        <ArrowDownRight size={16} className="ms-1" />
                      )}
                    </span>
                  </div>
                  <div className="text-muted small">{stat.title}</div>
                  <div className="h4 mb-0 mt-1">{stat.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts Section */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Répartition par montant</h5>
                  <BarChart3 size={20} className="text-muted" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={invoiceDistribution}
                      dataKey="value"
                      nameKey="range"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {invoiceDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Montant mensuel</h5>
                  <BarChart3 size={20} className="text-muted" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyInvoices}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Invoice Table */}
        <Row className="g-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Liste des factures</h5>
                  <Button variant="primary" onClick={() => handleOpenModal(null)}>
                    <PlusCircle size={20} className="me-2" />
                    Ajouter une facture
                  </Button>
                </div>
                <Table striped hover responsive className="table-invoices">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Montant</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.date}</td>
                        <td>{invoice.amount}</td>
                        <td>{invoice.status}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenModal(invoice)}
                          >
                            <Edit2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{currentInvoice ? 'Modifier la facture' : 'Ajouter une facture'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSaveInvoice}>
            <Modal.Body>
              <Form.Group className="mb-3" controlId="formInvoiceDate">
                <Form.Label>Date</Form.Label>
                <Form.Control type="date" defaultValue={currentInvoice?.date || ''} required />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formInvoiceAmount">
                <Form.Label>Montant</Form.Label>
                <Form.Control type="text" defaultValue={currentInvoice?.amount || ''} required /> 
              </Form.Group>
              <Form.Group className="mb-3" controlId="formInvoiceStatus">
                <Form.Label>Statut</Form.Label>
                <Form.Select defaultValue={currentInvoice?.status || 'En attente'} required>
                  <option value="Payée">Payée</option>
                  <option value="En attente">En attente</option>
                  <option value="Annulée">Annulée</option>
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button variant="primary" type="submit">
                Enregistrer
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Container>
  );
}

export default InvoiceDashboard;
